import { diagnosticsApi, type DxReportTemplateDto } from "@/api/diagnostics";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/report-templates";
const LIST_KEY = ["dx-report-templates"];

export function DiagnosticsReportTemplatesPage() {
  return (
    <MasterListPage<DxReportTemplateDto>
      breadcrumbs={["Diagnostics", "Masters"]}
      title="Report templates"
      subtitle="Header and interpretation boilerplate applied when rendering a lab result."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listReportTemplates(inc)}
      deleteFn={diagnosticsApi.deleteReportTemplate}
      basePath={BASE}
      newLabel="New template"
      labelOf={(r) => r.name}
      deleteNote="It will be refused while any lab test still uses it."
      columns={[
        { field: "code", headerName: "Code", width: 140 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        {
          field: "body", headerName: "Body", flex: 1, minWidth: 200,
          valueFormatter: (v: string | null | undefined) =>
            !v ? "—" : v.length > 60 ? `${v.slice(0, 60)}…` : v,
        },
        statusColumn<DxReportTemplateDto>(),
      ]}
    />
  );
}

interface Values { code: string; name: string; body: string; isActive: boolean }

export function ReportTemplateFormPage() {
  return (
    <MasterFormPage<Values, DxReportTemplateDto>
      breadcrumbs={["Diagnostics", "Report templates"]}
      noun="report template"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-report-template", id]}
      defaultValues={{ code: "", name: "", body: "", isActive: true }}
      getFn={diagnosticsApi.getReportTemplate}
      toValues={(d) => ({ code: d.code, name: d.name, body: d.body ?? "", isActive: d.isActive })}
      createFn={(v) => diagnosticsApi.createReportTemplate({
        code: v.code.trim(), name: v.name.trim(), body: v.body.trim() || null,
      })}
      updateFn={(id, v) => diagnosticsApi.updateReportTemplate(id, {
        code: v.code.trim(), name: v.name.trim(), body: v.body.trim() || null, isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <TextFieldCell control={control} name="code" label="Code" span={4} required />
          <TextFieldCell control={control} name="name" label="Name" span={8} required />
          <TextFieldCell control={control} name="body" label="Body (header / interpretation boilerplate)"
            span={12} multiline minRows={4} />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
